package kz.dentalcrm.app.core.ui.components.tooth

import androidx.compose.foundation.Canvas
import androidx.compose.foundation.gestures.detectTapGestures
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.BoxWithConstraints
import androidx.compose.foundation.layout.aspectRatio
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Modifier
import androidx.compose.ui.geometry.Offset
import androidx.compose.ui.geometry.Rect
import androidx.compose.ui.geometry.Size
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.graphics.Path
import androidx.compose.ui.graphics.drawscope.Stroke
import androidx.compose.ui.graphics.drawscope.scale
import androidx.compose.ui.graphics.drawscope.translate
import androidx.compose.ui.graphics.vector.PathParser
import androidx.compose.ui.input.pointer.pointerInput
import androidx.compose.ui.layout.onSizeChanged
import androidx.compose.ui.unit.toSize

import androidx.compose.ui.text.TextStyle
import androidx.compose.ui.text.drawText
import androidx.compose.ui.text.rememberTextMeasurer
import androidx.compose.ui.unit.sp
import kz.dentalcrm.app.expectations.rememberHapticFeedback

data class Tooth(
    val id: String,
    val path: Path,
    val bounds: Rect
)

@Composable
fun ToothSelector(
    modifier: Modifier = Modifier,
    selectedTeeth: List<String>,
    onToothClick: (String) -> Unit,
    multiSelect: Boolean = true,
    showPrimary: Boolean = false,
    showPermanent: Boolean = true,
    selectedColor: Color = Color.Blue,
    unselectedColor: Color = Color.LightGray,
    strokeColor: Color = Color.Black,
    strokeWidth: Float = 2f,
    enabled: Boolean = true,
    textStyle: TextStyle = TextStyle(
        color = Color.Black,
        fontSize = 10.sp
    )
) {
    val textMeasurer = rememberTextMeasurer()
    val hapticFeedback = rememberHapticFeedback()
    val teeth = remember(showPrimary, showPermanent) {
        TeethPaths.paths.filter { (key, _) ->
            val id = key.toIntOrNull() ?: 0
            (showPrimary || id < 50) && (showPermanent || id > 50)
        }.map { (key, d) ->
            val path = PathParser().parsePathString(d).toPath()
            Tooth(key, path, path.getBounds())
        }
    }

    // Calculate the total bounds of all teeth to scale and center them
    val totalBounds = remember(teeth) {
        if (teeth.isEmpty()) Rect.Zero else {
            var minX = Float.MAX_VALUE
            var minY = Float.MAX_VALUE
            var maxX = Float.MIN_VALUE
            var maxY = Float.MIN_VALUE
            
            teeth.forEach { 
                minX = minOf(minX, it.bounds.left)
                minY = minOf(minY, it.bounds.top)
                maxX = maxOf(maxX, it.bounds.right)
                maxY = maxOf(maxY, it.bounds.bottom)
            }
            Rect(minX, minY, maxX, maxY)
        }
    }

    BoxWithConstraints(modifier = modifier) {
        var canvasSize by remember { mutableStateOf(Size.Zero) }

        Canvas(
            modifier = Modifier
                .fillMaxSize()
                .onSizeChanged { canvasSize = it.toSize() }
                .pointerInput(enabled) {
                    detectTapGestures { tapOffset ->
                        if (!enabled) return@detectTapGestures
                        // Transform tap offset back to original coordinates
                        if (totalBounds == Rect.Zero) return@detectTapGestures
                        
                        val scaleX = size.width / totalBounds.width
                        val scaleY = size.height / totalBounds.height
                        val scale = minOf(scaleX, scaleY) * 0.85f // Add more padding for text to prevent overlapping
                        
                        val translateX = (size.width - totalBounds.width * scale) / 2f - totalBounds.left * scale
                        val translateY = (size.height - totalBounds.height * scale) / 2f - totalBounds.top * scale

                        val originalPoint = Offset(
                            (tapOffset.x - translateX) / scale,
                            (tapOffset.y - translateY) / scale
                        )

                        // Find clicked tooth
                        // Since we don't have Path.contains, we use bounds as approximation
                        // To improve, we check distance to center for overlapping areas
                        val candidates = teeth.filter { tooth ->
                            tooth.bounds.contains(originalPoint)
                        }

                        val clickedTooth = if (candidates.size <= 1) {
                            candidates.firstOrNull()
                        } else {
                            // If multiple candidates, pick the one with center closest to tap
                            candidates.minByOrNull { tooth ->
                                val center = tooth.bounds.center
                                val dx = center.x - originalPoint.x
                                val dy = center.y - originalPoint.y
                                dx * dx + dy * dy
                            }
                        }

                        clickedTooth?.let {
                            hapticFeedback.performHapticFeedback()
                            onToothClick(it.id)
                        }
                    }
                }
        ) {
            if (totalBounds != Rect.Zero) {
                val scaleX = size.width / totalBounds.width
                val scaleY = size.height / totalBounds.height
                val scale = minOf(scaleX, scaleY) * 0.85f // Add more padding for text to prevent overlapping
                
                val translateX = (size.width - totalBounds.width * scale) / 2f - totalBounds.left * scale
                val translateY = (size.height - totalBounds.height * scale) / 2f - totalBounds.top * scale

                translate(left = translateX, top = translateY) {
                    scale(scale, pivot = Offset.Zero) {
                        teeth.forEach { tooth ->
                            val isSelected = selectedTeeth.contains(tooth.id)
                            drawPath(
                                path = tooth.path,
                                color = if (isSelected) selectedColor else unselectedColor
                            )
                            drawPath(
                                path = tooth.path,
                                color = strokeColor,
                                style = Stroke(width = strokeWidth / scale)
                            )
                            
                            // Draw text
                            val center = totalBounds.center
                            val toothCenter = tooth.bounds.center
                            val id = tooth.id.toIntOrNull() ?: 0
                            
                            val vecX = toothCenter.x - center.x
                            val vecY = toothCenter.y - center.y
                            val dist = kotlin.math.sqrt(vecX * vecX + vecY * vecY)
                            
                            val dirX = if (dist > 0) vecX / dist else 0f
                            val dirY = if (dist > 0) vecY / dist else 0f
                            
                            // Permanent teeth (11-48) go outward, Primary (51-85) go inward
                            val isPermanent = id < 50
                            val offset = if (isPermanent) 35f else -35f // Increased from 25f to prevent overlapping
                            
                            val textX = toothCenter.x + dirX * offset
                            val textY = toothCenter.y + dirY * offset
                            
                            val measuredText = textMeasurer.measure(
                                text = tooth.id,
                                style = textStyle.copy(fontSize = (textStyle.fontSize.value / scale).sp)
                            )
                            
                            drawText(
                                textLayoutResult = measuredText,
                                topLeft = Offset(
                                    textX - measuredText.size.width / 2,
                                    textY - measuredText.size.height / 2
                                )
                            )
                        }
                    }
                }
            }
        }
    }
}
